import axios from "axios";
import { toast } from "react-toastify";
import { fetchMedicalServices, isHospitalOrderNo, serviceGroupOf } from "./risIntegration";

// LIS (pathologysoftware.in) hand-off for lab tests — when the phlebotomist
// sends a sample to the in-house lab (lab-technician/IpdLabOrders).
//
// As with the RIS, the frontend only talks to our backend: `lis/patient_registration/`
// fills the patient's name / gender / phone / age / address from PatientRecord,
// stores a PatientLaboratoryTest row and forwards the patient/save call. The LIS
// later POSTs the report URL to `lis/lis_callback/`, which lands in test_report_url.
//
// One call per test: PatientLaboratoryTest holds a single medical_service_id and
// the callback returns one report URL per patientCode, so a combined call could
// link only one of its tests back to its order.

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL || "";

// LIS account codes, forwarded verbatim by the backend, which keeps no
// per-facility copy yet. These are the vendor's sample values — replace this
// block once the backend supplies them per facility.
// See this the hardcoded data so in future we can move it to the backend and fetch it per facility, rather than hardcoding it here.
const LIS_ACCOUNT = {
  CompanyCode: "COAS",
  TestCode: "SSSC-FBS",
  BranchCode: "1",
  LabId: 1,
  UserName: "admin",
};

const pad2 = (n) => String(n).padStart(2, "0");

const yyyymmdd = (d = new Date()) =>
  `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;

const round2 = (n) => Math.round(n * 100) / 100;

const isLabService = (svc) => serviceGroupOf(svc) === "LABORATORY";

const listFee = (svc) => {
  const fee = Number(svc.fee);
  return Number.isFinite(fee) ? fee : 0;
};

// `gross` is the charge before discount, `net` what the patient pays for it.
// Only sent once charged — OPD after payment, IPD on credit — so it goes as
// settled, like ISPAID "1" on the RIS side.
const buildPayload = ({ test, patientId, patientType, careContextId, referBy }) => {
  const { svc, gross, net, appointmentId, orderNo } = test;
  const discount = round2(gross - net);
  return {
    patient_id: patientId,
    ...(appointmentId ? { appointment_id: String(appointmentId) } : {}),
    carecontext_id: careContextId ? String(careContextId) : "",
    // Stored as the PatientLaboratoryTest order_no, so the test and its order share one id.
    order_no: isHospitalOrderNo(orderNo) ? String(orderNo).trim() : "",
    medical_service_id: String(svc.medical_service_id || ""),
    medical_service_name: svc.name || "",
    ReferBy: referBy,
    Panel: "SELF",
    PatientType: patientType,
    TotalAmt: gross,
    Discount: discount,
    Paid: net,
    UserName: LIS_ACCOUNT.UserName,
    BranchCode: LIS_ACCOUNT.BranchCode,
    CompanyCode: LIS_ACCOUNT.CompanyCode,
    Tests: [
      {
        LabId: LIS_ACCOUNT.LabId,
        // TEMP: hardcoded vendor test code until the service master stores the LIS code.
        TestCode: LIS_ACCOUNT.TestCode,
        TestName: svc.name || "",
        Rate: String(gross),
        Discount: String(discount),
        Net: String(net),
        ReportStatus: "Not Done",
        AgentCommission: "0",
        DoctorCommission: "0",
        ReportingDoctor: referBy,
        DeliveryDate: yyyymmdd(),
      },
    ],
  };
};

// Never throws — failures surface as toasts only.
async function sendLabTestsToLis({ tests, headers, ...context }) {
  const results = await Promise.all(
    tests.map(async (test) => {
      const name = test.svc.name || "";
      try {
        const res = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}lis/patient_registration/`,
          buildPayload({ test, ...context }),
          { headers },
        );
        return res?.data?.status === "Success"
          ? { ok: true, name }
          : { ok: false, name, message: res?.data?.message };
      } catch (err) {
        return { ok: false, name, message: err?.response?.data?.message || err?.message };
      }
    }),
  );

  const sent = results.filter((r) => r.ok);
  if (sent.length) {
    toast.success(`Lab order sent to LIS: ${sent.map((r) => r.name).join(", ")}`);
  }
  results
    .filter((r) => !r.ok)
    .forEach((r) => {
      console.error("LIS registration failed:", r.name, r.message);
      toast.warn(`LIS registration failed for ${r.name}${r.message ? `: ${r.message}` : ""}`);
    });
}

/** Every PatientLaboratoryTest row of a patient (UHID), newest first. */
export async function fetchPatientLisRecords(patientId, headers) {
  const res = await axios.get(
    `${REACT_APP_BACKEND_ROUTE_URL}lis/patient_lab_records/${encodeURIComponent(patientId)}/`,
    { headers },
  );
  return Array.isArray(res?.data?.data) ? res.data.data : [];
}

/**
 * Registers the Laboratory tests of a sample with the LIS once the
 * phlebotomist has sent it to the in-house lab — IPD, OPD and Emergency alike.
 * Each test goes at its service master rate: the lab board does not know the
 * bill, so no discount is sent. Never throws — failures surface as toasts only.
 *
 * @param {object} args
 * @param {string} args.patientId      UHID (PatientRecord.patient_id, e.g. "PAT-1314")
 * @param {string} args.patientType    "IPD" / "OPD" / "EMERGENCY", as the lab board has it
 * @param {Array}  args.serviceIds     the sample's MedicalService ids (UUID or integer PK)
 * @param {object} args.orderNos       { [serviceId]: LAB- order number } for those ids
 * @param {string} args.careContextId  visit / admission care context
 * @param {string} args.appointmentId  OPD appointment, if any
 * @param {string} args.doctorName     ordering doctor (ReferBy / ReportingDoctor)
 * @param {object} args.headers        request headers incl. Authorization
 */
export async function sendLabSampleToLis({
  patientId,
  patientType,
  serviceIds,
  orderNos = {},
  careContextId,
  appointmentId,
  doctorName,
  headers,
}) {
  if (!patientId || !serviceIds.length) return;

  let services;
  try {
    services = await fetchMedicalServices(serviceIds, headers);
  } catch (err) {
    console.error("LIS service lookup failed:", err);
    toast.warn("Could not send lab order to LIS.");
    return;
  }

  const tests = services.filter(isLabService).map((svc) => {
    const fee = listFee(svc);
    // The board's id may be the integer PK (IPD) or the UUID (OPD).
    const orderNo = orderNos[String(svc.id)] ?? orderNos[String(svc.medical_service_id)];
    return { svc, gross: fee, net: fee, appointmentId, orderNo };
  });
  if (!tests.length) return;

  return sendLabTestsToLis({
    tests,
    patientId,
    patientType,
    careContextId,
    referBy: doctorName,
    headers,
  });
}
